import { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {

  Loader2, ArrowRight, Info, MapPin,
  Send, Shield, X, AlertTriangle, Car, Train, Leaf
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { SEO } from '../components/SEO';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Legend, Area, ComposedChart
} from 'recharts';
import { getApiBase } from '../lib/apiBase';

const API_URL = getApiBase();

const CEMENT_TYPES = [
  { value: 'CEM_I', label: 'CEM I (Portland)' },
  { value: 'CEM_II_A_L', label: 'CEM II/A-L (6-20% limestone)' },
  { value: 'CEM_II_B_L', label: 'CEM II/B-L (21-35% limestone)' },
  { value: 'CEM_III_A', label: 'CEM III/A (36-65% GGBS)' },
  { value: 'CEM_III_B', label: 'CEM III/B (66-80% GGBS)' },
  { value: 'OTHER', label: 'Other (CEM II/C-M, CEM VI, LC3...)' },
];

const WC_RATIOS = [0.40, 0.45, 0.50, 0.55, 0.60];

const COVER_OPTIONS = [20, 25, 30, 35, 40, 45, 50];

const SCENARIOS = [
  {
    id: 'tunnel',
    label: 'Tunnel',
    sub: 'Road or rail tunnel — sheltered, moderate humidity',
    icon: Train,
    defaults: { rh_mean: 75, curing_days: 3 },
    color: 'emerald',
  },
  {
    id: 'car_park',
    label: 'Car Park / Podium',
    sub: 'Multi-storey car park or under-podium area',
    icon: Car,
    defaults: { rh_mean: 70, curing_days: 1 },
    color: 'blue',
  },
];

// ─── City Search (shared pattern with thermal) ───────────────────────
function CitySearch({ onSelect, selected }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounce = useRef(null);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback((q) => {
    if (q.length < 2) { setResults([]); return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=8&language=en`);
        const data = await res.json();
        setResults(data.results || []);
        setOpen(true);
      } catch { setResults([]); }
      setLoading(false);
    }, 300);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          data-testid="durability-city-search"
          placeholder="Search for a city..."
          value={selected ? `${selected.name}, ${selected.country}` : query}
          onChange={(e) => {
            if (selected) onSelect(null);
            setQuery(e.target.value);
            search(e.target.value);
          }}
          onFocus={() => results.length > 0 && setOpen(true)}
          className="pl-10 pr-10 border-gray-200 text-sm"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />}
        {selected && (
          <button onClick={() => { onSelect(null); setQuery(''); }} className="absolute right-3 top-1/2 -translate-y-1/2">
            <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {results.map((r, i) => (
            <button
              key={i}
              data-testid={`durability-city-result-${i}`}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-50 last:border-0"
              onClick={() => {
                onSelect({ name: r.name, country: r.country || '', lat: r.latitude, lon: r.longitude, admin1: r.admin1 || '' });
                setQuery('');
                setOpen(false);
              }}
            >
              <span className="font-medium text-gray-800">{r.name}</span>
              {r.admin1 && <span className="text-gray-400">, {r.admin1}</span>}
              <span className="text-gray-400"> — {r.country}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Reliability badge ────────────────────────────────────────────────
function ReliabilityBadge({ beta, rh }) {
  const lowCorrosionRisk = rh != null && rh < 75;
  let cls, label, bg;

  if (lowCorrosionRisk) {
    // Dry XC3: corrosion rates negligible below ~75% RH
    // β ≥ 0.5 acceptable per von Greve-Dierfeld & Gehlen (2016)
    if (beta >= 1.5) { cls = 'RC1'; label = 'Adequate'; bg = 'bg-green-100 text-green-700'; }
    else if (beta >= 0.5) { cls = '—'; label = 'Acceptable (dry)'; bg = 'bg-blue-100 text-blue-700'; }
    else { cls = '—'; label = 'Not Adequate'; bg = 'bg-red-100 text-red-700'; }
  } else {
    // Humid XC3 (RH ≥ 75%): sustained corrosion risk — strict thresholds
    if (beta >= 3.8) { cls = 'RC3'; label = 'Very High'; bg = 'bg-green-100 text-green-700'; }
    else if (beta >= 3.3) { cls = 'RC2'; label = 'High'; bg = 'bg-green-100 text-green-700'; }
    else if (beta >= 1.5) { cls = 'RC1'; label = 'Adequate'; bg = 'bg-green-100 text-green-700'; }
    else if (beta >= 1.28) { cls = 'RC1'; label = 'Acceptable'; bg = 'bg-amber-100 text-amber-700'; }
    else { cls = '—'; label = 'Not Adequate'; bg = 'bg-red-100 text-red-700'; }
  }
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${bg}`}>{cls} — {label}</span>;
}

// ─── Main Component ───────────────────────────────────────────────────
export const CarbonationCalculator = () => {
  const resultsRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [selectedCity, setSelectedCity] = useState(null);
  const [fetchingClimate, setFetchingClimate] = useState(false);
  const [scenario, setScenario] = useState('car_park');

  const [params, setParams] = useState({
    cover_nominal: 35,
    wc_ratio: 0.50,
    cement_type: 'CEM_I',
    rh_mean: 70,
    curing_days: 1,
  });

  const set = (k, v) => setParams(p => ({ ...p, [k]: v }));

  // Fetch mean annual RH when city changes
  useEffect(() => {
    if (!selectedCity) return;
    const fetchClimate = async () => {
      setFetchingClimate(true);
      try {
        const year = new Date().getFullYear() - 1;
        const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${selectedCity.lat}&longitude=${selectedCity.lon}&start_date=${year}-01-01&end_date=${year}-12-31&daily=relative_humidity_2m_mean&timezone=auto`;
        const res = await fetch(url);
        const data = await res.json();
        if (data.daily?.relative_humidity_2m_mean) {
          const vals = data.daily.relative_humidity_2m_mean.filter(v => v !== null);
          if (vals.length) {
            const avgRH = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
            set('rh_mean', avgRH);
          }
        }
      } catch (e) { console.error('Humidity fetch failed', e); }
      setFetchingClimate(false);
    };
    fetchClimate();
  }, [selectedCity]);

  // Apply scenario defaults
  const handleScenario = (id) => {
    setScenario(id);
    const sc = SCENARIOS.find(s => s.id === id);
    if (sc) {
      setParams(p => ({ ...p, ...sc.defaults }));
    }
  };

  const handleCalculate = async () => {
    if (params.cement_type === 'OTHER') { setError('specialist'); return; }
    if (!selectedCity) { setError('Please select a city to determine the ambient humidity.'); return; }
    setLoading(true);
    setError('');
    setResults(null);
    try {
      const body = {
        ...params,
        city_name: selectedCity ? `${selectedCity.name}, ${selectedCity.country}` : null,
        city_latitude: selectedCity?.lat || null,
        city_longitude: selectedCity?.lon || null,
        scenario,
      };
      const res = await fetch(`${API_URL}/api/calculator/carbonation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => null);
        if (res.status === 429) {
          throw new Error(
            d?.detail ||
              "You've reached the daily lookup limit. Need more? Contact us at info@durabledesigns.co.uk and we'll lift the cap.",
          );
        }
        throw new Error(d?.detail || 'Calculation failed');
      }
      setResults(await res.json());
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth' }), 200);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const r50 = results?.results?.find(r => r.year === 50);
  const alt = results?.alternative_comparison;

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <SEO title="Service Life Estimator | Durable Designs" description="Estimate carbonation-induced corrosion probability for XC3 exposure. Interactive durability verification for tunnels, car parks and podium structures." />

      {/* Hero */}
      <section className="relative pt-24 pb-14 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ background: '#06120B' }}>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium tracking-wide mb-6" style={{ background: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.2)' }}>
            <Shield className="w-3.5 h-3.5" /> XC3 Exposure — Carbonation
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tighter leading-[1.08] mb-4" data-testid="durability-heading">
            Service Life Estimator
          </h1>
          <p className="text-base text-neutral-400 leading-relaxed max-w-2xl">
            Will your concrete cover last 50 years against carbonation? Pick your mix,
            cover depth and location — see the probability of corrosion initiation instantly.
            Each calculation automatically compares your cement against a lower-carbon
            alternative, showing the trade-off between durability and sustainability.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* ── Input Panel ── */}
            <div className="lg:col-span-2 space-y-5">

              {/* Scenario Toggle */}
              <div data-testid="scenario-toggle">
                <Label className="text-xs text-gray-500 mb-2 block">Design scenario (XC3 — sheltered from rain)</Label>
                <div className="grid grid-cols-2 gap-3">
                  {SCENARIOS.map(sc => {
                    const active = scenario === sc.id;
                    return (
                      <button
                        key={sc.id}
                        data-testid={`scenario-${sc.id}`}
                        onClick={() => handleScenario(sc.id)}
                        className={`flex items-start gap-3 rounded-lg border p-4 text-left transition-all ${
                          active
                            ? 'border-green-500 bg-green-50 ring-1 ring-green-500'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <sc.icon className={`w-6 h-6 mt-0.5 ${active ? 'text-green-600' : 'text-gray-400'}`} />
                        <div>
                          <p className={`text-sm font-semibold ${active ? 'text-green-800' : 'text-gray-700'}`}>{sc.label}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{sc.sub}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Location */}
              <Card className="border-gray-200">
                <CardContent className="pt-5 pb-5 space-y-4">
                  <h3 className="text-sm font-bold text-gray-800 tracking-tight flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-green-600" /> Location
                  </h3>
                  <CitySearch selected={selectedCity} onSelect={setSelectedCity} />
                  {selectedCity && (
                    <div>
                      {fetchingClimate && <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading mean annual humidity...</p>}
                      {!fetchingClimate && <p className="text-xs text-gray-400 mt-1">Mean annual RH: <strong className="text-gray-600">{params.rh_mean}%</strong></p>}
                    </div>
                  )}
                  {!selectedCity && (
                    <p className="text-xs text-gray-400 italic">Select a city to determine the mean annual relative humidity for your location.</p>
                  )}
                </CardContent>
              </Card>

              {/* Mix Design */}
              <Card className="border-gray-200">
                <CardContent className="pt-5 pb-5 space-y-4">
                  <h3 className="text-sm font-bold text-gray-800 tracking-tight">Mix Design</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Cement type</Label>
                      <select
                        data-testid="durability-cement-type"
                        value={params.cement_type}
                        onChange={e => set('cement_type', e.target.value)}
                        className="mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        {CEMENT_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Water / cement ratio</Label>
                      <div className="flex gap-1.5 mt-1">
                        {WC_RATIOS.map(wc => (
                          <button
                            key={wc}
                            data-testid={`wc-${wc}`}
                            onClick={() => set('wc_ratio', wc)}
                            className={`flex-1 py-2 text-xs font-semibold rounded transition-colors ${
                              params.wc_ratio === wc
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >{wc}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Cover Depth */}
              <Card className="border-gray-200">
                <CardContent className="pt-5 pb-5 space-y-3">
                  <h3 className="text-sm font-bold text-gray-800 tracking-tight">Nominal Concrete Cover</h3>
                  <div className="flex gap-2 flex-wrap">
                    {COVER_OPTIONS.map(c => (
                      <button
                        key={c}
                        data-testid={`cover-${c}`}
                        onClick={() => set('cover_nominal', c)}
                        className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                          params.cover_nominal === c
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >{c} mm</button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Calculate */}
              <Button
                data-testid="durability-calculate-btn"
                onClick={handleCalculate}
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 text-sm font-semibold"
              >
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calculating...</> : 'Estimate Service Life'}
              </Button>

              {error === 'specialist' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-5" data-testid="specialist-notice">
                  <div className="flex gap-3">
                    <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">Specialist Cement Type</p>
                      <p className="text-sm text-amber-700 mt-1">
                        CEM II/C-M, CEM VI, and LC3 (calcined clay) cements require specialist calibration
                        not yet included in this tool. Their carbonation behaviour differs significantly
                        from standard binder systems.
                      </p>
                      <Link to="/engineering/contact" className="inline-flex items-center gap-1 text-sm font-semibold text-amber-800 mt-3 hover:underline">
                        Contact our team for advice <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
              {error && error !== 'specialist' && (
                <p className="text-sm text-red-600 text-center" data-testid="durability-error">{error}</p>
              )}
            </div>

            {/* ── Info Sidebar ── */}
            <div className="space-y-4">
              <Card className="border-gray-200 bg-gray-50">
                <CardContent className="pt-5 pb-5">
                  <h3 className="text-sm font-bold text-gray-800 mb-3">What is XC3?</h3>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    <strong>Moderate humidity, sheltered from rain.</strong> Carbonation is the dominant
                    degradation mechanism. CO&#8322; from the atmosphere lowers the concrete pH until
                    the protective oxide layer on the reinforcement is destroyed, initiating corrosion.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-gray-200 bg-gray-50">
                <CardContent className="pt-5 pb-5">
                  <h3 className="text-sm font-bold text-gray-800 mb-3">Key Factors</h3>
                  <ul className="space-y-2 text-xs text-gray-600">
                    <li><strong className="text-gray-700">Cover depth</strong> — the physical barrier protecting the steel.</li>
                    <li><strong className="text-gray-700">w/c ratio</strong> — lower ratio = denser concrete = slower carbonation.</li>
                    <li><strong className="text-gray-700">Cement type</strong> — high-slag cements carbonate faster due to lower Ca(OH)&#8322; reserves.</li>
                    <li><strong className="text-gray-700">Humidity</strong> — carbonation is fastest at 50-70% RH.</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border-blue-100 bg-blue-50/40">
                <CardContent className="pt-5 pb-5">
                  <h3 className="text-sm font-bold text-gray-800 mb-3">Durability vs. Sustainability</h3>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Lower-carbon cements reduce embodied CO&#8322; but often carbonate faster due
                    to reduced Ca(OH)&#8322; reserves — potentially shortening the service life of
                    the cover zone. A concrete that appears sustainable on a per-tonne basis may
                    actually deliver <strong className="text-gray-700">more carbon per year of useful life</strong> if
                    it requires earlier repair or replacement.
                  </p>
                  <p className="text-xs text-gray-600 leading-relaxed mt-2">
                    This tool lets you visualise that trade-off: every calculation automatically
                    compares your chosen cement against a lower-carbon alternative, showing both
                    the corrosion probability curve and the <strong className="text-gray-700">carbon intensity per year
                    of service</strong> — so you can make an informed decision rather than relying
                    on embodied carbon alone.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-amber-100 bg-amber-50/50">
                <CardContent className="pt-5 pb-5">
                  <p className="text-xs text-amber-700 leading-relaxed">
                    <strong>BS 8500-1:2023</strong> now uses strength as the primary durability criterion
                    for XC exposure, accommodating low-carbon binders. However, the long-term carbonation
                    performance of newer binder systems still warrants independent validation — particularly
                    for high-replacement cements. Our specialists can help verify that your low-carbon mix
                    meets the required service life.
                  </p>
                  <Link to="/engineering/contact" className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 mt-2 hover:underline">
                    Speak to a specialist <ArrowRight className="w-3 h-3" />
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* ─── Results ─── */}
          {results?.success && (
            <div ref={resultsRef} className="mt-12 space-y-6" data-testid="durability-results">
              <h2 className="text-xl font-bold text-gray-900 tracking-tight">Results — 50 Year Assessment</h2>

              {/* Summary cards */}
              <div className="grid sm:grid-cols-3 gap-4">
                <div className={`rounded-lg border p-4 ${r50 && r50.pf_form > 0.1 ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'}`} data-testid="metric-pf50">
                  <p className="text-xs text-gray-500">Probability of Corrosion at 50 years</p>
                  <p className={`text-3xl font-bold tracking-tight mt-1 ${r50 && r50.pf_form > 0.1 ? 'text-red-700' : 'text-gray-900'}`}>
                    {r50 ? `${(r50.pf_form * 100).toFixed(1)}%` : '—'}
                  </p>
                  {r50 && r50.pf_form > 0.1 && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Exceeds 10% target</p>
                  )}
                </div>
                <div className={`rounded-lg border p-4 ${
                  r50 && params.rh_mean < 75
                    ? (r50.beta < 0.5 ? 'border-red-200 bg-red-50' : r50.beta < 1.5 ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50')
                    : (r50 && r50.beta < 1.28 ? 'border-red-200 bg-red-50' : r50 && r50.beta < 1.5 ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50')
                }`} data-testid="metric-beta50">
                  <p className="text-xs text-gray-500">Reliability Index at 50 years</p>
                  <p className={`text-3xl font-bold tracking-tight mt-1 ${
                    r50 && params.rh_mean < 75
                      ? (r50.beta < 0.5 ? 'text-red-700' : r50.beta < 1.5 ? 'text-blue-700' : 'text-gray-900')
                      : (r50 && r50.beta < 1.28 ? 'text-red-700' : r50 && r50.beta < 1.5 ? 'text-amber-700' : 'text-gray-900')
                  }`}>{r50 ? r50.beta.toFixed(2) : '—'}</p>
                  {r50 && <div className="mt-1.5"><ReliabilityBadge beta={r50.beta} rh={params.rh_mean} /></div>}
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4" data-testid="metric-xc50">
                  <p className="text-xs text-gray-500">Mean Carbonation Depth at 50 years</p>
                  <p className="text-3xl font-bold tracking-tight mt-1 text-gray-900">{r50 ? `${r50.carbonation_depth_mean.toFixed(1)} mm` : '—'}</p>
                  <p className="text-xs text-gray-400 mt-1">Cover: {params.cover_nominal} mm</p>
                </div>
              </div>

              {/* RH-corrosion context note */}
              {r50 && params.rh_mean < 75 && r50.beta < 1.28 && r50.beta >= 0.5 && (
                <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-4" data-testid="rh-corrosion-note">
                  <div className="flex gap-2.5">
                    <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                    <div className="space-y-1.5">
                      <p className="text-xs font-semibold text-gray-700">Low corrosion risk environment (RH {params.rh_mean?.toFixed(0)}%)</p>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        At mean annual RH below ~75%, corrosion rates in carbonated concrete are negligible — even where carbonation
                        has reached the reinforcement. In these dry XC3 conditions, a reliability index as low
                        as 0.5 may be acceptable, as the probability of <em>sustained</em> corrosion
                        is very low (von Greve-Dierfeld &amp; Gehlen, 2016).
                      </p>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        However, in humid climates (mean RH &ge; 75%, e.g. Hong Kong, Singapore) corrosion rates become
                        significant once carbonation reaches the steel — stricter reliability thresholds (&beta; &ge; 1.28) are
                        justified. <strong className="text-gray-700">Specialist assessment is recommended</strong> for borderline
                        environments or structures with mixed exposure.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {r50 && params.rh_mean >= 75 && r50.beta < 1.28 && (
                <div className="rounded-lg border border-red-100 bg-red-50/40 p-4" data-testid="high-rh-warning">
                  <div className="flex gap-2.5">
                    <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-600 leading-relaxed">
                      <strong className="text-red-700">High corrosion risk environment (RH {params.rh_mean?.toFixed(0)}%):</strong> At
                      this humidity level, corrosion in carbonated concrete is sustained and potentially rapid.
                      A reliability index below 1.28 does not meet the minimum requirement for this exposure.
                      Increase cover depth, reduce the w/c ratio, or consult our specialists.
                    </p>
                  </div>
                </div>
              )}

              {/* Pf Chart */}
              <Card className="border-gray-200">
                <CardContent className="pt-5 pb-5">
                  <h3 className="text-sm font-bold text-gray-800 mb-4">Probability of Corrosion Initiation Over Time</h3>
                  <div className="h-[300px]" data-testid="pf-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={results.results.map((r, idx) => {
                          const point = { year: r.year, pf: r.pf_form * 100, xc: r.carbonation_depth_mean };
                          if (alt?.alt_results?.[idx]) {
                            point.pf_alt = alt.alt_results[idx].pf_form * 100;
                          }
                          return point;
                        })}
                        margin={{ top: 5, right: 20, bottom: 40, left: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="year" label={{ value: 'Years', position: 'insideBottom', offset: -10, style: { fontSize: 11, fill: '#888' } }} tick={{ fontSize: 10 }} />
                        <YAxis label={{ value: 'Pf (%)', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#888' } }} tick={{ fontSize: 10 }} domain={[0, 'auto']} />
                        <Tooltip contentStyle={{ fontSize: 11 }} formatter={(v, name) => {
                          if (name === 'pf') return [`${v.toFixed(2)}%`, alt ? alt.main_label : 'P(corrosion)'];
                          if (name === 'pf_alt') return [`${v.toFixed(2)}%`, alt?.alt_label || 'Alternative'];
                          return [`${v.toFixed(1)} mm`, 'Carb. depth'];
                        }} labelFormatter={l => `Year ${l}`} />
                        <ReferenceLine y={10} stroke="#ef4444" strokeDasharray="6 3" label={{ value: '10% target', fill: '#ef4444', fontSize: 10, position: 'right' }} />
                        <Line type="monotone" dataKey="pf" stroke="#16a34a" strokeWidth={2.5} dot={{ fill: '#16a34a', r: 5 }} activeDot={{ r: 7 }} name="pf" />
                        {alt && <Line type="monotone" dataKey="pf_alt" stroke="#2563eb" strokeWidth={2} strokeDasharray="6 3" dot={{ fill: '#2563eb', r: 4 }} activeDot={{ r: 6 }} name="pf_alt" />}
                        {alt && <Legend formatter={(value) => value === 'pf' ? alt.main_label : value === 'pf_alt' ? `${alt.alt_label} (lower carbon)` : value} wrapperStyle={{ fontSize: 11, paddingTop: 16 }} />}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Lower-carbon alternative comparison */}
              {alt && (
                <Card className="border-blue-100 bg-blue-50/30" data-testid="carbon-comparison">
                  <CardContent className="pt-5 pb-5 space-y-4">
                    <div className="flex items-center gap-2">
                      <Leaf className="w-5 h-5 text-blue-600" />
                      <h3 className="text-sm font-bold text-gray-800">Sustainability Comparison</h3>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      {/* Selected cement */}
                      <div className="rounded-lg border border-gray-200 bg-white p-4">
                        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Selected</p>
                        <p className="text-sm font-bold text-gray-800">{alt.main_label}</p>
                        <div className="mt-3 space-y-1.5 text-xs text-gray-600">
                          <div className="flex justify-between"><span>CO&#8322; per tonne</span><span className="font-semibold text-gray-800">{alt.main_co2} kg</span></div>
                          <div className="flex justify-between"><span>f<sub>cm,28,cube</sub></span><span className="font-semibold text-gray-800">{alt.main_f28} MPa</span></div>
                          <div className="flex justify-between"><span>f<sub>cm,56,cube</sub></span><span className="font-semibold text-gray-800">{alt.main_f56} MPa</span></div>
                          <div className="flex justify-between"><span>Est. strength class</span><span className="font-semibold text-gray-800">{alt.main_strength_class || '—'}</span></div>
                          <div className="flex justify-between"><span>Est. service life</span><span className="font-semibold text-gray-800">{alt.main_service_life > 50 ? '>50' : alt.main_service_life} yrs</span></div>
                          <div className="flex justify-between border-t border-gray-100 pt-1.5 mt-1.5">
                            <span>kg CO&#8322; / yr</span>
                            <span className="font-semibold text-gray-800">{alt.main_carbon_intensity}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>kg CO&#8322; / (MPa<sub>cube,28</sub> &times; yr)</span>
                            <span className="font-semibold text-gray-800">{alt.main_combined_efficiency_28}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>kg CO&#8322; / (MPa<sub>cube,56</sub> &times; yr)</span>
                            <span className="font-bold text-gray-800">{alt.main_combined_efficiency}</span>
                          </div>
                        </div>
                      </div>

                      {/* Alternative cement */}
                      <div className={`rounded-lg border p-4 ${alt.alt_service_life < 50 ? 'border-red-200 bg-red-50/50' : 'border-blue-200 bg-blue-50/50'}`}>
                        <p className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${alt.alt_service_life < 50 ? 'text-red-500' : 'text-blue-500'}`}>Lower Carbon Alternative</p>
                        <p className={`text-sm font-bold ${alt.alt_service_life < 50 ? 'text-red-800' : 'text-blue-800'}`}>{alt.alt_label}</p>
                        <div className="mt-3 space-y-1.5 text-xs text-gray-600">
                          <div className="flex justify-between"><span>CO&#8322; per tonne</span><span className={`font-semibold ${alt.alt_service_life < 50 ? 'text-red-800' : 'text-blue-800'}`}>{alt.alt_co2} kg</span></div>
                          <div className="flex justify-between"><span>f<sub>cm,28,cube</sub></span><span className={`font-semibold ${alt.alt_service_life < 50 ? 'text-red-800' : 'text-blue-800'}`}>{alt.alt_f28} MPa</span></div>
                          <div className="flex justify-between"><span>f<sub>cm,56,cube</sub></span><span className={`font-semibold ${alt.alt_service_life < 50 ? 'text-red-800' : 'text-blue-800'}`}>{alt.alt_f56} MPa</span></div>
                          <div className="flex justify-between"><span>Est. strength class</span><span className={`font-semibold ${alt.alt_service_life < 50 ? 'text-red-800' : 'text-blue-800'}`}>{alt.alt_strength_class || '—'}</span></div>
                          <div className="flex justify-between">
                            <span>Est. service life</span>
                            <span className={`font-semibold ${alt.alt_service_life < 50 ? 'text-red-700' : 'text-blue-800'}`}>
                              {alt.alt_service_life > 50 ? '>50' : alt.alt_service_life} yrs
                              {alt.alt_service_life < 50 && <span className="text-[10px] ml-1 font-bold">FAILS</span>}
                            </span>
                          </div>
                          <div className={`flex justify-between border-t pt-1.5 mt-1.5 ${alt.alt_service_life < 50 ? 'border-red-100' : 'border-blue-100'}`}>
                            <span>kg CO&#8322; / yr</span>
                            <span className={`font-semibold ${alt.alt_service_life < 50 ? 'text-red-800' : 'text-blue-800'}`}>{alt.alt_carbon_intensity}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>kg CO&#8322; / (MPa<sub>cube,28</sub> &times; yr)</span>
                            <span className={`font-semibold ${alt.alt_service_life < 50 ? 'text-red-800' : 'text-blue-800'}`}>{alt.alt_combined_efficiency_28}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>kg CO&#8322; / (MPa<sub>cube,56</sub> &times; yr)</span>
                            <span className={`font-bold ${alt.alt_service_life < 50 ? 'text-red-800' : 'text-blue-800'}`}>{alt.alt_combined_efficiency}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Interpretation */}
                    <div className="rounded bg-white/80 border border-gray-100 p-3">
                      <p className="text-xs text-gray-600 leading-relaxed" data-testid="carbon-interpretation">
                        {(() => {
                          const carbonSaved = alt.carbon_saving_pct;
                          const slDiff = alt.alt_service_life - alt.main_service_life;
                          const mainEff = alt.main_combined_efficiency;
                          const altEff = alt.alt_combined_efficiency;
                          const betterCombined = altEff && mainEff && altEff < mainEff;
                          const altFails50 = alt.alt_service_life < 50;
                          const dry = alt.dry_climate;
                          const thresholdNote = dry
                            ? <> (service life assessed at &beta; &ge; 0.5 — low corrosion risk at RH {alt.rh_mean?.toFixed(0)}%)</>
                            : <> (service life assessed at &beta; &ge; 1.28 — sustained corrosion risk at RH {alt.rh_mean?.toFixed(0)}%)</>;

                          if (altFails50) {
                            return <><strong className="text-red-700">{alt.alt_label} does not achieve the 50-year design life</strong> at the current cover depth and w/c ratio — the estimated service life is only {alt.alt_service_life} years{thresholdNote}. While it saves {carbonSaved}% of cement CO&#8322;, this is not a viable substitution without increasing cover depth, reducing the w/c ratio, or accepting a shorter design life. <strong>Contact our specialists</strong> for a project-specific optimisation.</>;
                          } else if (betterCombined && slDiff >= 0) {
                            return <>Switching from <strong>{alt.main_label}</strong> to <strong>{alt.alt_label}</strong> saves <strong>{carbonSaved}%</strong> of cement CO&#8322; with no loss in service life{thresholdNote}. The combined efficiency improves from {mainEff} to {altEff} kg CO&#8322;/(MPa<sub>cube,56</sub>&middot;yr) — a clear win on all three dimensions: carbon, strength, and durability.</>;
                          } else if (betterCombined) {
                            return <>Switching to <strong>{alt.alt_label}</strong> saves <strong>{carbonSaved}%</strong> of cement CO&#8322;, though the estimated service life reduces by ~{Math.abs(slDiff).toFixed(0)} years due to faster carbonation{thresholdNote}. Despite this, the combined efficiency still improves from {mainEff} to {altEff} kg CO&#8322;/(MPa<sub>cube,56</sub>&middot;yr) — meaning each unit of strength and service delivers less carbon.</>;
                          } else {
                            return <>Switching to <strong>{alt.alt_label}</strong> saves <strong>{carbonSaved}%</strong> of cement CO&#8322;, but the faster carbonation rate and lower 56-day strength increase the combined carbon efficiency from {mainEff} to {altEff} kg CO&#8322;/(MPa<sub>cube,56</sub>&middot;yr){thresholdNote}. To recover performance, consider increasing cover depth or reducing the w/c ratio. <strong>Contact our specialists</strong> for a project-specific optimisation.</>;
                          }
                        })()}
                      </p>
                    </div>

                    <p className="text-[10px] text-gray-400 leading-relaxed">
                      Cube strength estimates (f<sub>cm,cube</sub>) derived from cylinder mean strength (f<sub>cm,cyl</sub> / 0.8)
                      using Abrams' law (28 d) with Eurocode 2 time-development coefficients (56 d).
                      Actual values depend on aggregate, admixtures, and curing regime — verify with trial mixes.
                    </p>

                  </CardContent>
                </Card>
              )}

              {/* Results table */}
              <Card className="border-gray-200">
                <CardContent className="p-0">
                  <table className="w-full text-sm" data-testid="durability-table">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Year</th>
                        <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500">P<sub>f</sub></th>
                        <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500">Beta</th>
                        <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500">Reliability</th>
                        <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500">x<sub>c</sub> (mm)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.results.map((r, i) => (
                        <tr key={r.year} className={i % 2 === 0 ? '' : 'bg-gray-50/50'}>
                          <td className="px-4 py-2 font-medium">{r.year}</td>
                          <td className="px-4 py-2 text-center">{r.pf_form === 0 ? '0%' : r.pf_form < 0.001 ? '<0.1%' : `${(r.pf_form * 100).toFixed(1)}%`}</td>
                          <td className="px-4 py-2 text-center">{r.beta >= 10 ? '—' : r.beta.toFixed(2)}</td>
                          <td className="px-4 py-2 text-center"><ReliabilityBadge beta={r.beta} rh={params.rh_mean} /></td>
                          <td className="px-4 py-2 text-center">{r.carbonation_depth_mean.toFixed(1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              {/* Carbonation Depth Evolution Chart */}
              <Card className="border-gray-200" data-testid="depth-chart-card">
                <CardContent className="pt-5 pb-5">
                  <h3 className="text-sm font-bold text-gray-800 mb-1">Carbonation Depth Evolution</h3>
                  <p className="text-[11px] text-gray-400 mb-4">Mean depth with 95% confidence interval — reinforcement location shown at cover depth</p>
                  <div className="h-[300px]" data-testid="depth-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={results.results.map(r => ({
                          year: r.year,
                          mean: r.carbonation_depth_mean,
                          ci95: [r.carbonation_depth_p5 ?? 0, r.carbonation_depth_p95 ?? 0],
                          p95: r.carbonation_depth_p95,
                        }))}
                        margin={{ top: 5, right: 20, bottom: 20, left: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="year" label={{ value: 'Years', position: 'insideBottom', offset: -10, style: { fontSize: 11, fill: '#888' } }} tick={{ fontSize: 10 }} />
                        <YAxis label={{ value: 'Depth (mm)', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#888' } }} tick={{ fontSize: 10 }} domain={[0, (max) => Math.max(max, params.cover_nominal + 5)]} />
                        <Tooltip contentStyle={{ fontSize: 11 }} formatter={(v, name) => {
                          if (name === 'mean') return [`${v.toFixed(1)} mm`, 'Mean depth'];
                          if (name === 'p95') return [`${v.toFixed(1)} mm`, '95th percentile'];
                          return [null, null];
                        }} labelFormatter={l => `Year ${l}`} />
                        <ReferenceLine y={params.cover_nominal} stroke="#dc2626" strokeDasharray="8 4" strokeWidth={1.5} label={{ value: `Reinforcement (${params.cover_nominal} mm)`, fill: '#dc2626', fontSize: 10, position: 'right' }} />
                        <Area type="monotone" dataKey="ci95" stroke="none" fill="#16a34a" fillOpacity={0.10} name="95% CI" />
                        <Line type="monotone" dataKey="p95" stroke="#16a34a" strokeWidth={1} strokeDasharray="4 3" dot={false} name="p95" />
                        <Line type="monotone" dataKey="mean" stroke="#16a34a" strokeWidth={2.5} dot={{ fill: '#16a34a', r: 4 }} activeDot={{ r: 6 }} name="mean" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* CTA */}
              <Card className="border-gray-200 bg-gray-50">
                <CardContent className="p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Need a full durability report?</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Probabilistic simulations, multiple exposure classes, and service life beyond 50 years.</p>
                  </div>
                  <Link to="/engineering/contact">
                    <Button className="bg-green-600 hover:bg-green-700 text-white text-sm px-5 whitespace-nowrap">
                      Request Full Analysis <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Practical interpretation */}
              <Card className="border-gray-200">
                <CardContent className="pt-5 pb-5 space-y-3">
                  <h3 className="text-sm font-bold text-gray-800">What does this mean in practice?</h3>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Even a probability below the 10% target represents a tangible risk: at the end
                    of the design life, approximately 10% of the exposed surface area may have
                    experienced corrosion initiation — leading to cracking, spalling, and staining.
                    For structures with long utilisation periods, this can represent a significant
                    economic burden, as repair costs include not only direct remediation but also
                    potential service unavailability and disruption.
                  </p>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    We strongly recommend that <strong className="text-gray-800">confirmation testing</strong> be
                    carried out to validate the assumed carbonation performance, particularly for
                    non-standard binder systems. Our specialists can design an appropriate testing
                    programme and interpret the results in the context of your project.
                  </p>
                  <Link to="/engineering/contact" className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline">
                    Arrange a consultation <ArrowRight className="w-3 h-3" />
                  </Link>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-12 border-t border-gray-100 pt-8">
            <div className="flex gap-3 text-xs text-gray-400 leading-relaxed max-w-3xl">
              <Shield className="w-4 h-4 shrink-0 mt-0.5 text-gray-300" />
              <p>
                <strong className="text-gray-500">Disclaimer:</strong> This tool provides indicative estimates
                for preliminary assessment purposes only based on the fib Model Code 34 methodology.
                Results should not be relied upon for detailed design or construction decisions.
                Durable Designs accepts no liability for any loss arising from the use of this tool.
                For project-specific durability verification, independent specialist analysis is essential.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CarbonationCalculator;
